import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Bell } from 'lucide-react';

import {
  PICK_TYPE_LABELS,
  getOperationalMix,
  type CarrierMixEntry,
  type OperationalMixResult,
  type PickTypeBreakdownEntry,
} from '@/api/shipedgeReports.api';
import { Card } from '@/components/ui/Card';
import { ShipedgeAccessDenied, ShipedgePageHeader } from './_chrome';
import { useShipedgeAdminGate } from './_gate';

/**
 * Operational Mix page. Phase 15 Day 7.
 *
 * Three side-by-side cards: pick-type breakdown, carrier mix,
 * internal-ops% headline. V1 uses Tailwind progress bars rather
 * than a chart library — communicates the same information without
 * the dependency tax. Adding Recharts is a clean follow-up if/when
 * a stakeholder wants donut/line charts.
 */

export function OperationalMixPage() {
  const { canAccess } = useShipedgeAdminGate();

  const [startDate, setStartDate] = useState(defaultStartDate);
  const [endDate, setEndDate] = useState(defaultEndDate);

  // Guard: cleared/partial date pickers must not fire requests the
  // server will 400. Tier 4 polish.
  const rangeValid =
    /^\d{4}-\d{2}-\d{2}$/.test(startDate) && /^\d{4}-\d{2}-\d{2}$/.test(endDate) && startDate <= endDate;

  const query = useQuery({
    queryKey: ['shipedge-operational-mix', startDate, endDate],
    queryFn: () => getOperationalMix({ startDate, endDate }),
    enabled: canAccess && rangeValid,
  });

  if (!canAccess) return <ShipedgeAccessDenied />;

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <ShipedgePageHeader
        title="Operational Mix"
        description='Pick-type breakdown, carrier distribution, internal-ops share. Surfaces "is the warehouse healthy?" at a glance.'
      />

      <div className="flex flex-wrap items-end gap-4">
        <DateInput label="Start date" value={startDate} onChange={setStartDate} />
        <DateInput label="End date" value={endDate} onChange={setEndDate} />
      </div>

      {query.isLoading ? (
        <div className="flex justify-center py-16">
          <div className="size-8 animate-spin rounded-full border-2 border-kw-cerulean border-t-transparent" />
        </div>
      ) : query.error ? (
        <ErrorCard error={query.error} />
      ) : !query.data || query.data.totalShipments === 0 ? (
        <Card className="flex flex-col items-center gap-3 p-12 text-center">
          <Bell className="size-10 text-kw-ghost" />
          <div>
            <p className="text-[15px] font-medium text-kw-shark">No shipments in this range</p>
            <p className="mt-1 text-[13px] text-kw-muted">Try a different window.</p>
          </div>
        </Card>
      ) : (
        <MixGrid data={query.data} />
      )}
    </div>
  );
}

function MixGrid({ data }: { data: OperationalMixResult }) {
  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
      <Card className="p-4">
        <p className="text-[11px] uppercase tracking-wide text-kw-muted">Pick-type breakdown</p>
        <p className="mt-1 text-[24px] font-semibold text-kw-shark">
          {data.totalShipments.toLocaleString()} shipments
        </p>
        <ul className="mt-4 space-y-2">
          {data.pickTypeBreakdown.map((entry) => (
            <PickTypeBar key={entry.pickType ?? 'null'} entry={entry} />
          ))}
        </ul>
      </Card>

      <Card className="p-4">
        <p className="text-[11px] uppercase tracking-wide text-kw-muted">Carrier mix</p>
        <p className="mt-1 text-[24px] font-semibold text-kw-shark">{data.carrierMix.length} carriers</p>
        <ul className="mt-4 space-y-2">
          {data.carrierMix.map((entry) => (
            <CarrierBar key={entry.carrier} entry={entry} />
          ))}
        </ul>
      </Card>

      <Card className="p-4">
        <p className="text-[11px] uppercase tracking-wide text-kw-muted">Internal ops share</p>
        <p className="mt-1 text-[36px] font-semibold text-kw-shark">{data.internalOpsPercent}%</p>
        <p className="mt-2 text-[13px] text-kw-muted">
          Share of shipments that didn&apos;t go to a customer — KITTING, SHIP-ON-SHELF, TRANSFER, DISPOSE,
          CLOSE-OUT all collapse here. A healthy warehouse usually runs ≤ 20%.
        </p>
      </Card>
    </div>
  );
}

function PickTypeBar({ entry }: { entry: PickTypeBreakdownEntry }) {
  const label = entry.pickType === null ? 'Unbucketed' : PICK_TYPE_LABELS[entry.pickType];
  const tooltip = entry.pickType === null ? 'Non-catalog box or unmatched REQUEST' : undefined;
  return (
    <li>
      <div className="flex items-baseline justify-between text-[12px] text-kw-shark">
        <span title={tooltip}>{label}</span>
        <span className="tabular-nums text-kw-muted">
          {entry.count.toLocaleString()} ({entry.percent}%)
        </span>
      </div>
      <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-kw-bg">
        <div
          className={`h-full ${entry.pickType === 'internal' ? 'bg-kw-warning-text' : 'bg-kw-cerulean'}`}
          style={{ width: `${parseFloat(entry.percent).toFixed(2)}%` }}
        />
      </div>
    </li>
  );
}

function CarrierBar({ entry }: { entry: CarrierMixEntry }) {
  return (
    <li>
      <div className="flex items-baseline justify-between text-[12px] text-kw-shark">
        <span>{entry.carrier}</span>
        <span className="tabular-nums text-kw-muted">
          {entry.count.toLocaleString()} ({entry.percent}%)
        </span>
      </div>
      <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-kw-bg">
        <div
          className="h-full bg-kw-cerulean"
          style={{ width: `${parseFloat(entry.percent).toFixed(2)}%` }}
        />
      </div>
    </li>
  );
}

function DateInput({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (next: string) => void;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[12px] font-medium text-kw-muted">{label}</span>
      <input
        type="date"
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
        }}
        className="rounded-kw-md border border-kw-border bg-white px-2.5 py-1.5 text-[13px] text-kw-shark focus:border-kw-cerulean focus:outline-none focus:ring-2 focus:ring-kw-cerulean/20"
      />
    </label>
  );
}

function ErrorCard({ error }: { error: unknown }) {
  return (
    <Card className="flex items-start gap-3 border-kw-danger-text/30 bg-kw-danger-bg p-4">
      <AlertTriangle className="size-5 shrink-0 text-kw-danger-text" />
      <div>
        <p className="text-[14px] font-medium text-kw-danger-text">Couldn&apos;t load report</p>
        <p className="mt-1 text-[13px] text-kw-danger-text/80">
          {error instanceof Error ? error.message : 'Unknown error'}
        </p>
      </div>
    </Card>
  );
}

// Default to last 30 days ending yesterday (calendar-day, no DST math —
// the server handles DST-aware comparisons).
function defaultEndDate(): string {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return isoDate(d);
}
function defaultStartDate(): string {
  const d = new Date();
  d.setDate(d.getDate() - 30);
  return isoDate(d);
}
function isoDate(d: Date): string {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${String(yyyy)}-${mm}-${dd}`;
}
